import React from "react";
import { useBandFokus } from "../../hooks/useBandFokus";
import { getIcon } from "../../utils/icon-mapping";

/**
 * Ein vertikal wanderndes Band aus Bausteinen, das ihren Zustand zeigt
 * (12.9.2026, Vorbild workos.com).
 *
 * **Die Bewegung ist die Aussage, nicht der Schmuck.** Ein Baustein steht auf
 * «Bereit» und geht auf «In Betrieb», waehrend das Band durchlaeuft — das Bild
 * zum Hero-Satz «Die Standardteile in Minuten. Ihre Fachlichkeit in Wochen.»
 *
 * Aufbau: Die Liste wird VERDOPPELT und um genau ihre halbe Hoehe verschoben.
 * Nach einem Durchlauf steht die Kopie exakt dort, wo das Original begann —
 * deshalb springt nichts. Dasselbe Verfahren wie in `CustomerLogoCarousel`, nur
 * in der anderen Achse.
 *
 * **Die Tiefenwirkung entsteht aus drei gestaffelten Groessen** (13.9.2026, an
 * `workos-hero-component.webm` nachgemessen), nicht aus einer Fokusklasse:
 *
 * | | Mitte | Nachbar | aussen |
 * |---|---|---|---|
 * | Deckkraft | 100 % | ~60 % | ~40 % |
 * | Groesse | 1.0 | 0.975 | 0.94 |
 * | Zustand | schaltet um | bleibt | bleibt |
 *
 * Die Deckkraft ist die wichtigste der drei: In der Vorlage misst die
 * Textschwaerze 58 in der Mitte gegen 136/147 bei den Nachbarn (Grund 252). Ein
 * Band mit gleich hellen Zeilen wirkt flach, auch wenn die Mitte skaliert ist.
 *
 * **Der Zustand kommt im Lauf allein vom Band, nicht aus dem Content.** In der
 * Vorlage sind nie mehr als ein bis zwei Etiketten gleichzeitig gruen
 * (ausgezaehlt ueber sieben Einzelbilder); jede Zeile faehrt als «Bereit»
 * herein, schaltet in der Mitte um und faellt am Rand zurueck.
 *
 * `item.aktiv` aus dem Content gilt deshalb NUR bei angehaltener Bewegung. Die
 * erste Fassung veroderte beides — dann trugen vier bis fuenf Zeilen
 * gleichzeitig «In Betrieb», und das Aufleuchten sagte nichts mehr aus.
 *
 * ⚠️ `prefers-reduced-motion` haelt das Band an — geregelt im zentralen Block
 * in `index.css`, NICHT hier per `motion-reduce:`. Zwei Mechanismen fuer
 * dieselbe Sache driften auseinander, sobald einer geaendert wird (WCAG 2.2.2).
 * Angehalten zeigt das Band alle Zeilen gleich und im Content-Zustand.
 */

export type Baustein = {
    icon: string;
    title: string;
    /** `true` = «In Betrieb», `false` = «Bereit». Siehe Kopfkommentar. */
    aktiv: boolean;
};

export type BausteinKarussellProps = {
    items: Baustein[];
    /** Beschriftung der beiden Zustaende — aus dem Content, nie hier fest verdrahtet. */
    labelAktiv: string;
    labelBereit: string;
    /**
     * Was ein Screenreader statt der Animation hoert. Das Band ist eine
     * Illustration; vorgelesen waeren vierundzwanzig Zeilen (Original plus
     * Kopie) nur Laerm.
     */
    ariaLabel: string;
    className?: string;
};

/** Die Grundklassen einer Bandzeile. Ausgelagert, damit die JSX-Zeile lesbar bleibt. */
const zeileKlassen = "baustein-zeile mb-3 flex items-center gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-black/5";

/**
 * Rechnet die Naehe zur Mitte (0..1) in die sichtbaren Groessen um.
 *
 * Ohne Messwert — vor dem ersten Bild und bei angehaltener Bewegung — gilt
 * `undefined`: Dann traegt die Zeile volle Deckkraft und keine Skalierung. Ein
 * Startwert von 0 liesse das Band beim ersten Rendern unsichtbar aufblitzen.
 *
 * **`tonung` staffelt die Zustandsfarben mit** (14.9.2026). Die Deckkraft der
 * Zeile daempft zwar alles darin, aber kraeftiges Violett bleibt auch bei 34 %
 * noch auffaellig — vier eingeschaltete Schalter in gleichem Ton lasen sich als
 * «alle an», obwohl die Zeilen unterschiedlich blass waren. In der Vorlage sind
 * die Schalter aktiver Zeilen verschieden dunkel (gemessen rgb(100,105,108) in
 * der Mitte gegen rgb(133,139,140) eine Zeile weiter).
 *
 * ⚠️ Die Tonung faellt auf einen SOCKEL, nicht auf null. Eine erste Fassung
 * rechnete `naehe * naehe` — dort verlor eine gerade eingeschaltete Zeile ihr
 * Violett schon beim Weiterfahren, und der Beleg fuer den Vorgang verschwand
 * mit ihr. Das Zuruecktreten leistet die Deckkraft der Zeile; die Zustandsfarbe
 * muss nur lesbar bleiben.
 */
const stufen = (naehe: number | undefined) => {
    if (naehe === undefined) {
        return { opacity: 1, scale: 1, schatten: "", tonung: 1, polster: 0 };
    }
    return {
        // 0.34 als Sockel: Die aeusseren Zeilen bleiben erkennbar, die Maske
        // uebernimmt das restliche Ausblenden zum Rand hin.
        opacity: 0.34 + 0.66 * naehe,
        // Spanne 0.88..1.00 — die Fokuszeile bleibt bei GENAU 1.
        //
        // ⚠️ Eine fruehere Fassung ging bis 1.06. `scale` wirkt proportional:
        // Bei 624 px Rahmenbreite waren das 37 px zu viel, und `overflow-hidden`
        // schnitt die Fokuszeile rechts ab. In der Hoehe brachten dieselben 6 %
        // nur 3 px — unsichtbar. Eine Skalierung kann nicht zugleich den Rahmen
        // sprengen und in der Hoehe wirken; die Hoehe macht deshalb `polster`.
        scale: 0.88 + 0.12 * naehe,
        // Zusaetzliche Hoehe fuer die Fokuszeile, als echter Innenabstand statt
        // Skalierung: 6 px oben und unten machen aus 47 px sichtbare 59 px,
        // ohne die Breite anzutasten.
        polster: Math.round(6 * naehe),
        schatten: naehe > 0.8 ? "baustein-zeile--gehoben" : "",
        // Die Zustandsfarbe folgt der Zeile, faellt aber nicht auf null: Ein
        // eingeschalteter Schalter, dessen Violett verschwindet, ist nicht mehr
        // als eingeschaltet zu erkennen — dann fehlt der Beleg fuer das, was
        // gerade geschehen ist. Der Sockel 0.45 haelt ihn lesbar, waehrend die
        // Deckkraft der Zeile das Zuruecktreten uebernimmt.
        tonung: 0.45 + 0.55 * naehe,
    };
};

export const BausteinKarussell: React.FC<BausteinKarussellProps> = ({
    items,
    labelAktiv,
    labelBereit,
    ariaLabel,
    className = "",
}) => {
    // Verdoppelt fuer den nahtlosen Umlauf — siehe Kopfkommentar.
    const band = [...items, ...items];
    // Hooks vor jedem bedingten Ausstieg (Rules of Hooks).
    const { bandRef, listeRef, zeileRef, naehe, aktivierte, beschriftet, springt } = useBandFokus({
        zeilenAnzahl: band.length,
    });

    /**
     * Keine Messwerte heisst: Das Band bewegt sich nicht — entweder beim
     * Server-Rendern, vor dem ersten Bild, oder weil `prefers-reduced-motion`
     * die Schleife gar nicht erst startet. Dann traegt jede Zeile ihren
     * Content-Zustand, damit die Aussage «sechs laufen bereits» auch ohne
     * Bewegung ankommt.
     */
    const laeuftStill = naehe.length === 0;

    if (!items.length) {
        return null;
    }

    return (
        <div
            className={`relative overflow-hidden ${className}`}
            role="img"
            aria-label={ariaLabel}
            data-testid="baustein-karussell"
        >
            {/* Die Maske blendet nach oben und unten aus. Sie ist weicher als
                bei WorkOS (dort 22%), weil das Band hier auf DUNKLEM Grund steht:
                Auf Weiss verschwindet eine Kachel im Nichts, auf Violett bleibt
                ein Schemen stehen, wenn zu frueh ausgeblendet wird.

                ⚠️ **28rem sind SIEBEN Zeilen a 64px — die ungerade Zahl ist der
                Punkt.** Bei 26rem passten 6.5 Zeilen ins Fenster, die Bandmitte
                lag also ZWISCHEN zwei Zeilenpositionen: Nie stand eine Zeile
                mittig, immer zwei je einen halben Abstand daneben. Beide trugen
                fast denselben Naehewert (gemessen 0.96 und 0.73), und keine
                Kurvenschaerfung half dagegen — das Problem ist Geometrie, nicht
                Rechnung. Wer die Hoehe aendert, muss ein ungerades Vielfaches
                des Zeilenabstands waehlen. */}
            <div
                ref={bandRef}
                className="relative h-[28rem]"
                style={{
                    maskImage: "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
                    WebkitMaskImage: "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
                }}
            >
                {/* Die Position setzt `useBandFokus` je Bild — das Band laeuft in
                    Schritten mit Pausen, nicht gleichmaessig. Eine CSS-Animation
                    braeuchte dafuer zwei Stuetzstellen je Zeile, fest verdrahtet
                    an die Zeilenzahl. */}
                {/* `baustein-band--sprung` friert die Uebergaenge waehrend des
                    Ruecksprungs ein — sonst faehrt der Schalter dreier Zeilen
                    unten sichtbar zurueck (Begruendung in `useBandFokus`). */}
                <ul ref={listeRef} aria-hidden="true" className={springt ? "baustein-band--sprung" : ""}>
                    {band.map((item, idx) => {
                        const { opacity, scale, schatten, tonung, polster } = stufen(naehe[idx]);
                        // NUR der Banddurchlauf schaltet um — `item.aktiv` gilt
                        // allein bei angehaltener Bewegung (siehe `laeuftStill`).
                        // Beides zu verodern liess vier bis fuenf Etiketten
                        // gleichzeitig leuchten; dann sagt das Aufleuchten nichts
                        // mehr. In der Vorlage sind es nie mehr als zwei.
                        // Der Zustand wird je Bild aus der POSITION abgeleitet
                        // (`zeilenSindAn`), nicht gespeichert — deshalb genuegt
                        // hier der Zeilenindex, und Original wie Kopie stimmen
                        // von selbst ueberein.
                        const laeuft = laeuftStill ? item.aktiv : aktivierte.has(idx);
                        // Das Etikett folgt dem Schalter mit eigenem Abstand:
                        // Der Schalter ist die Handlung, «In Betrieb» ihre
                        // Folge. Eine `transition-delay` reicht dafuer nicht —
                        // sie verzoegert die Farbe, nicht das Wort.
                        const beschriftetAn = laeuftStill ? item.aktiv : beschriftet.has(idx);
                        return (
                            <li
                                key={idx}
                                ref={zeileRef(idx)}
                                className={`${zeileKlassen} ${schatten}`}
                                style={
                                    {
                                        opacity,
                                        transform: `scale(${scale})`,
                                        paddingTop: `calc(0.75rem + ${polster}px)`,
                                        paddingBottom: `calc(0.75rem + ${polster}px)`,
                                        // Steuert die Staerke der Zustandsfarben —
                                        // Schalter und Etikett lesen sie im CSS.
                                        "--baustein-tonung": tonung,
                                    } as React.CSSProperties
                                }
                            >
                                {/* Der Kippschalter traegt den Zustand zusammen mit
                                    dem Etikett. In der Vorlage ist er das
                                    auffaelligere der beiden — er sitzt am
                                    Zeilenanfang, wo der Blick zuerst hinfaellt. */}
                                <span className={`baustein-schalter ${laeuft ? "baustein-schalter--an" : ""}`}>
                                    <span className="baustein-schalter__knopf" />
                                </span>
                                {/* Lucide statt Emoji: gleiche Strichstaerke wie im
                                    Kachelraster, und die Farbe folgt der Zeile
                                    (`currentColor` unter der Deckkraft). Emoji trugen
                                    ihre eigenen Farben und stachen aus dem Band. */}
                                {(() => {
                                    const ZeilenIcon = getIcon(item.icon);
                                    return (
                                        <ZeilenIcon
                                            aria-hidden="true"
                                            className="h-4 w-4 shrink-0 text-primary"
                                            strokeWidth={1.75}
                                        />
                                    );
                                })()}
                                <span className="flex-1 text-sm font-semibold text-gray-900">{item.title}</span>
                                <span
                                    className={`baustein-etikett rounded-full px-2.5 py-1 text-xs font-semibold ${
                                        beschriftetAn ? "baustein-etikett--an" : "bg-neutral-100 text-neutral-500"
                                    }`}
                                >
                                    {beschriftetAn ? labelAktiv : labelBereit}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

export default BausteinKarussell;
